<template>
  <div>

    <!-- ===== VISTA LISTA SERRE ===== -->
    <template v-if="vista === 'lista'">
      <div class="px-4 pt-4 pb-2">
        <div class="text-subtitle-1 font-weight-bold mb-3">Piano di Coltivazione</div>
        <v-select
          v-model="stagione_attiva"
          :items="stagioni"
          :item-text="s => `${s.tipo_stagione ? s.tipo_stagione.nome : ''} ${s.anno}`.trim()"
          return-object
          label="Stagione"
          outlined dense hide-details
          :loading="loading_bootstrap"
          @change="onStagioneChange"
        />
      </div>

      <v-progress-linear v-if="loading_bootstrap" indeterminate />

      <template v-else-if="serre_ordinate.length">
        <v-list two-line class="pt-0">
          <template v-for="(serra, i) in serre_ordinate">
            <v-list-item :key="serra.id" @click="onSerraSelezionata(serra)">
              <v-list-item-avatar tile size="36" class="mr-3 d-flex align-center justify-center">
                <div :style="{
                  width: '22px', height: '22px', borderRadius: '4px',
                  backgroundColor: serraFill(serra.tipo),
                  border: `2px solid ${serraColor(serra.tipo)}`,
                }" />
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{ serra.nome }}</v-list-item-title>
                <v-list-item-subtitle>
                  <template v-if="file_info_serre[serra.id]">
                    {{ file_info_serre[serra.id].file }} file · {{ file_info_serre[serra.id].vasi }} vasi
                  </template>
                  <span v-else class="grey--text">—</span>
                </v-list-item-subtitle>
                <v-progress-linear
                  v-if="completamento_serre[serra.id] != null"
                  :value="completamento_serre[serra.id]"
                  :color="completamento_serre[serra.id] >= 80 ? 'green darken-1' : completamento_serre[serra.id] >= 40 ? 'orange darken-1' : 'grey'"
                  height="3"
                  rounded
                  class="mt-1"
                  style="max-width: 160px"
                />
              </v-list-item-content>

              <v-list-item-action class="align-end">
                <span
                  v-if="completamento_serre[serra.id] != null"
                  class="caption font-weight-medium mb-1"
                  :class="completamento_serre[serra.id] >= 80 ? 'green--text text--darken-1' : 'grey--text'"
                >{{ completamento_serre[serra.id] }}%</span>
                <v-icon color="grey lighten-1" small>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-divider
              v-if="i < serre_ordinate.length - 1"
              :key="'d-' + serra.id"
              inset
            />
          </template>
        </v-list>
      </template>

      <v-alert v-else type="info" outlined class="ma-4">
        Nessuna serra configurata.
      </v-alert>
    </template>

    <!-- ===== VISTA DETTAGLIO SERRA ===== -->
    <template v-else-if="vista === 'serra' && serra_selezionata">
      <!-- Toolbar back -->
      <v-toolbar flat dense color="white" class="px-0">
        <v-btn icon @click="onBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="text-subtitle-1 font-weight-bold pl-0">
          {{ serra_selezionata.nome }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="$refs.gestione_serre.openDialogConSerra(serra_selezionata.id)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider />

      <!-- Loading snapshot -->
      <v-progress-linear v-if="loading_snapshot" indeterminate />

      <template v-else>
        <!-- Stato vuoto: nessuna fila configurata -->
        <div
          v-if="!file_stagione_corrente.length"
          class="d-flex flex-column align-center justify-center pa-10"
        >
          <v-icon size="48" color="grey lighten-1" class="mb-4">mdi-view-sequential</v-icon>
          <div class="text-body-2 grey--text text--darken-1 mb-5 text-center">
            Nessuna fila configurata per questa stagione.
          </div>
          <v-btn
            outlined
            color="primary"
            @click="$refs.gestione_serre.openDialogConSerra(serra_selezionata.id)"
          >
            <v-icon small class="mr-1">mdi-pencil</v-icon>
            Configura file
          </v-btn>
        </div>

        <!-- Mappa visiva file -->
        <SerraMappaView
          v-else
          :serra="serra_selezionata"
          :tipo="serra_selezionata.tipo"
          :file_stagione="file_stagione_corrente"
          :specie="specie_disponibili"
          :snapshot="snapshot"
          :prodotti_pianificazione="prodotti_pianificazione"
          :prodotto_labels="prodotto_labels"
          :contenitori="contenitori_coltivazione"
          orientation="vertical"
          :show_avanserra="false"
          @fila-selezionata="onFilaSelezionata"
        />

        <!-- Recap prodotti -->
        <div v-if="recap_prodotti.righe.length" class="px-4 pb-6">
          <div class="caption grey--text text--darken-1 font-weight-bold mb-2 mt-2">RIEPILOGO PRODOTTI</div>
          <v-card outlined>
            <v-simple-table dense>
              <tbody>
                <tr v-for="item in recap_prodotti.righe" :key="item.id">
                  <td style="width:30px; padding-right:0; padding-left:12px; vertical-align:middle">
                    <template v-if="!item.component_infos || item.component_infos.length <= 1">
                      <img v-if="item.img_url" :src="item.img_url" class="fila-swatch" />
                      <div v-else class="fila-swatch" :style="{ backgroundColor: item.color }" />
                    </template>
                    <svg v-else class="fila-swatch" viewBox="0 0 22 22" width="22" height="22">
                      <defs>
                        <clipPath
                          v-for="(inf, idx) in item.component_infos"
                          :key="`cpmob-${item.id}-${idx}`"
                          :id="`pmob-${item.id}-${idx}`"
                        >
                          <path :d="_sectorPath(idx, item.component_infos.length, 11, 11, 11)"/>
                        </clipPath>
                      </defs>
                      <g v-for="(inf, idx) in item.component_infos" :key="`secmob-${item.id}-${idx}`">
                        <image v-if="inf.img_url" :href="inf.img_url" x="0" y="0" width="22" height="22"
                               :clip-path="`url(#pmob-${item.id}-${idx})`" preserveAspectRatio="xMidYMid slice"/>
                        <path v-else :d="_sectorPath(idx, item.component_infos.length, 11, 11, 11)"
                              :fill="inf.fallback"/>
                      </g>
                      <circle cx="11" cy="11" r="10.5" fill="none" stroke="rgba(0,0,0,0.12)" stroke-width="1"/>
                    </svg>
                  </td>
                  <td style="padding-top:5px; padding-bottom:5px; line-height:1.3">
                    <div v-if="item.tipo_contenitore" class="text-caption grey--text text--darken-1" style="line-height:1;margin-bottom:1px">{{ item.tipo_contenitore }}</div>
                    <div v-if="item.specie_nomi" class="text-caption grey--text font-italic" style="line-height:1">{{ item.specie_nomi }}</div>
                    <div v-for="(riga, ri) in item.righe_dettaglio" :key="ri" class="text-caption">{{ riga }}</div>
                    <div v-if="!item.righe_dettaglio || !item.righe_dettaglio.length" class="text-caption">{{ item.label }}</div>
                  </td>
                  <td class="text-caption text-right grey--text" style="white-space:nowrap; vertical-align:middle">{{ item.diametro ? `Ø${item.diametro}` : '—' }}</td>
                  <td class="text-caption text-right font-weight-medium pr-3" style="white-space:nowrap; vertical-align:middle">{{ item.vasi }}v</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="grey lighten-4">
                  <td colspan="2" class="caption pl-3">
                    Liberi: {{ recap_prodotti.liberi }} · Tot: {{ recap_prodotti.totale_vasi }}
                  </td>
                  <td colspan="2" class="caption text-right font-weight-medium pr-3">
                    {{ recap_prodotti.totale_vasi > 0
                        ? Math.round(recap_prodotti.occupati / recap_prodotti.totale_vasi * 100)
                        : 0 }}%
                  </td>
                </tr>
              </tfoot>
            </v-simple-table>
          </v-card>
        </div>
      </template>
    </template>

    <!-- Snackbar feedback -->
    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" bottom timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>

    <!-- Dialog gestione serre (condiviso col desktop) -->
    <GestioneSerreDialog
      ref="gestione_serre"
      :solo_form="true"
      :stagione_attiva="stagione_attiva"
      @serre-aggiornate="onSerreAggiornate"
    />

    <!-- ProdottoPicker (dialog condiviso col desktop) -->
    <ProdottoPicker
      v-model="dialog_picker"
      :fila="fila_selezionata"
      :file="file_serra_ordinate"
      :snapshot_fila="snapshot_fila"
      :snapshot_fila_precedente="snapshot_fila_precedente"
      :prodotti_pianificazione="prodotti_pianificazione"
      :prodotto_labels="prodotto_labels"
      :specie="specie_disponibili"
      :contenitori="contenitori_coltivazione"
      :loading="loading_posizione"
      :save_error="errore_posizione"
      :save_success="successo_posizione"
      @naviga-fila="onFilaSelezionata"
      @posizione-salvata="onPosizioneSalvata"
      @posizione-eliminata="onPosizioneEliminata"
    />
  </div>
</template>

<script>
import colorUtility from "../Mixin/colorUtility.js";
import specieLookup from "../Mixin/specieLookup.js";
import ProdottoPicker from "../Desktop/ProdottoPicker.vue";
import SerraMappaView from "../Desktop/SerraMappaView.vue";
import GestioneSerreDialog from "../Desktop/GestioneSerreDialog.vue";

const TIPO_LABELS = {
  ferrovetro:   'Ferrovetro',
  film:         'Film plastico',
  ombreggiante: 'Ombreggiante',
  campo_aperto: 'Campo aperto',
};
const TIPO_COLORS = {
  ferrovetro:   '#1565C0',
  film:         '#E65100',
  ombreggiante: '#4527A0',
  campo_aperto: '#2E7D32',
};
const TIPO_FILLS = {
  ferrovetro:   '#E3F2FD',
  film:         '#FFF8E1',
  ombreggiante: '#EDE7F6',
  campo_aperto: '#E8F5E9',
};

export default {
  name: "PianoColtivazioneMobile",
  components: { ProdottoPicker, SerraMappaView, GestioneSerreDialog },
  mixins: [colorUtility, specieLookup],
  data() {
    return {
      vista:             'lista',
      stagione_attiva:   null,
      serra_selezionata: null,
      fila_selezionata:  null,
      dialog_picker:     false,
      loading_bootstrap: false,
      loading_snapshot:  false,
      loading_posizione:  false,
      errore_posizione:   '',
      successo_posizione: false,
      snackbar:          { visible: false, color: 'success', message: '' },
    };
  },
  computed: {
    stagioni() {
      return this.$store.getters.getStagioniColtivazione;
    },
    serre() {
      return (this.$store.getters.getSerre || []).filter(s => !s.dismessa);
    },
    serre_ordinate() {
      return [...this.serre].sort((a, b) => (a.posizione ?? Infinity) - (b.posizione ?? Infinity));
    },
    specie_disponibili() {
      return this.$store.getters.get_specie || [];
    },
    contenitori_coltivazione() {
      return this.$store.getters.getContenitoriColtivazione || [];
    },
    prodotti_pianificazione() {
      if (!this.stagione_attiva) return [];
      return this.$store.getters.getProdottiPianificazione(this.stagione_attiva.id);
    },
    snapshot() {
      if (!this.serra_selezionata || !this.stagione_attiva) return [];
      return this.$store.getters.getSerraSnapshot(
        this.serra_selezionata.id,
        this.stagione_attiva.id
      );
    },
    snapshot_fila() {
      if (!this.fila_selezionata) return [];
      return this.snapshot.filter((p) => p.id_fila_stagione === this.fila_selezionata.id);
    },
    snapshot_fila_precedente() {
      if (!this.fila_selezionata) return [];
      const prev = this.file_stagione_corrente.find(
        (f) => f.numero === this.fila_selezionata.numero - 1
      );
      if (!prev) return [];
      return this.snapshot.filter((p) => p.id_fila_stagione === prev.id);
    },
    prodotto_map() {
      const map = {};
      for (const p of this.prodotti_pianificazione) map[p.id] = p;
      return map;
    },
    prodotto_labels() {
      const map = {};
      for (const p of this.prodotti_pianificazione) {
        const dettaglio = this.getComponentiDettaglio(p);
        const nomi = dettaglio.map((d) => d.label).join(' / ');
        const cont = this.contenitori_coltivazione.find((c) => c.id === p.id_contenitore);
        const diam = cont ? ` Ø${cont.diametro}` : '';
        map[p.id] = (nomi || `P${p.id}`) + diam;
      }
      return map;
    },
    file_stagione_corrente() {
      if (!this.serra_selezionata || !this.stagione_attiva) return [];
      return this.$store.getters.getFileStagione(
        this.serra_selezionata.id,
        this.stagione_attiva.id
      );
    },
    file_serra_ordinate() {
      return [...this.file_stagione_corrente].sort((a, b) => a.numero - b.numero);
    },
    file_info_serre() {
      if (!this.stagione_attiva) return {};
      const map = {};
      for (const serra of this.serre) {
        const file = this.$store.getters.getFileStagione(serra.id, this.stagione_attiva.id);
        if (!file.length) continue;
        map[serra.id] = {
          file: file.length,
          vasi: file.reduce((a, f) => a + (f.num_vasi || 0), 0),
        };
      }
      return map;
    },
    completamento_serre() {
      if (!this.stagione_attiva) return {};
      const map = {};
      for (const serra of this.serre) {
        const id = serra.id;
        if (!this.$store.getters.isSnapshotCached(id, this.stagione_attiva.id)) continue;
        const file = this.$store.getters.getFileStagione(id, this.stagione_attiva.id);
        const num_vasi = file.reduce((a, f) => a + (f.num_vasi || 0), 0);
        const filled = this.$store.getters.getSerraSnapshot(id, this.stagione_attiva.id)
          .reduce((a, p) => a + (p.vaso_fine - p.vaso_inizio + 1), 0);
        map[id] = num_vasi > 0 ? Math.round((filled / num_vasi) * 100) : 0;
      }
      return map;
    },
    recap_prodotti() {
      const totale_vasi = this.file_stagione_corrente
        .reduce((a, f) => a + (f.num_vasi || 0), 0);
      const ids_order = [];
      const per_prodotto = {};
      for (const pos of this.snapshot) {
        const id = pos.id_prodotto_pianificazione;
        if (per_prodotto[id] === undefined) { per_prodotto[id] = 0; ids_order.push(id); }
        per_prodotto[id] += pos.vaso_fine - pos.vaso_inizio + 1;
      }
      const occupati = ids_order.reduce((a, id) => a + per_prodotto[id], 0);
      const righe = ids_order.map((id) => {
        const prodotto = this.prodotto_map[id] || null;
        const vasi = per_prodotto[id];
        const cont = prodotto
          ? this.contenitori_coltivazione.find((c) => c.id === prodotto.id_contenitore)
          : null;
        const riepilogo = this._componenteRiepilogo(prodotto, cont);
        return {
          id,
          label: (this.prodotto_labels[id] || `P${id}`).replace(/\s*Ø\d+.*$/, ''),
          color: this.productColor(prodotto),
          img_url: this._varietaImgUrl(prodotto),
          component_infos: this._componenteInfos(prodotto),
          tipo_contenitore: riepilogo.tipo_contenitore,
          specie_nomi: riepilogo.specie_nomi,
          righe_dettaglio: riepilogo.righe,
          diametro: cont ? cont.diametro : null,
          vasi,
        };
      }).sort((a, b) => b.vasi - a.vasi);
      return { righe, occupati, totale_vasi, liberi: totale_vasi - occupati };
    },
  },
  mounted() {
    this.loading_bootstrap = true;
    Promise.all([
      this.$store.dispatch('fetchStagioniColtivazione'),
      this.$store.dispatch('fetchSerre'),
      this.$store.dispatch('fetchContenitoriColtivazione'),
      this.$store.dispatch('fetch_specie'),
    ]).then(() => {
      this._autoSelectStagione();
    }).finally(() => {
      this.loading_bootstrap = false;
    });
  },
  methods: {
    _autoSelectStagione() {
      const stagioni = this.stagioni;
      if (!stagioni.length) return;
      const now = Math.floor(Date.now() / 1000);
      const corrente = stagioni.find((s) => s.inizio <= now && s.fine >= now);
      this.stagione_attiva = corrente || stagioni[stagioni.length - 1];
      if (this.stagione_attiva) {
        this.$store.dispatch('fetchProdottiPianificazione', this.stagione_attiva.id);
        this._fetchFileTutteSerre();
      }
    },
    onStagioneChange() {
      if (this.stagione_attiva) {
        this.$store.dispatch('fetchProdottiPianificazione', this.stagione_attiva.id);
        this._fetchFileTutteSerre();
      }
      if (this.vista === 'serra') this.onBack();
    },
    _fetchFileTutteSerre() {
      if (!this.stagione_attiva) return;
      for (const serra of this.serre) {
        if (!this.$store.getters.isFileStagioneCached(serra.id, this.stagione_attiva.id)) {
          this.$store.dispatch('fetchFileStagione', {
            id_serra: serra.id,
            id_stagione: this.stagione_attiva.id,
          });
        }
      }
    },
    onSerraSelezionata(serra) {
      this.serra_selezionata = serra;
      this.vista = 'serra';
      if (!this.stagione_attiva) return;
      this.loading_snapshot = true;
      Promise.all([
        this.$store.dispatch('fetchSerraSnapshot', {
          id_serra:    serra.id,
          id_stagione: this.stagione_attiva.id,
        }),
        this.$store.dispatch('fetchFileStagione', {
          id_serra:    serra.id,
          id_stagione: this.stagione_attiva.id,
        }),
      ]).finally(() => { this.loading_snapshot = false; });
    },
    onBack() {
      this.vista             = 'lista';
      this.serra_selezionata = null;
      this.fila_selezionata  = null;
    },
    onFilaSelezionata(fila) {
      this.fila_selezionata   = fila;
      this.errore_posizione   = '';
      this.successo_posizione = false;
      this.dialog_picker      = true;
    },
    async onPosizioneSalvata(payload) {
      if (!this.serra_selezionata || !this.stagione_attiva) return;
      this.loading_posizione = true;
      this.errore_posizione  = '';
      try {
        await this.$store.dispatch('savePosizioneSerra', {
          id_serra:    this.serra_selezionata.id,
          id_stagione: this.stagione_attiva.id,
          payload,
        });
        this.successo_posizione = true;
        setTimeout(() => { this.successo_posizione = false; }, 3000);
      } catch (e) {
        this.errore_posizione = e?.response?.data?.message || 'Errore durante il salvataggio.';
      } finally {
        this.loading_posizione = false;
      }
    },
    async onPosizioneEliminata({ id_posizione }) {
      if (!this.serra_selezionata || !this.stagione_attiva) return;
      try {
        await this.$store.dispatch('deletePosizioneSerra', {
          id_posizione,
          id_serra:    this.serra_selezionata.id,
          id_stagione: this.stagione_attiva.id,
        });
      } catch {
        this.mostraSnackbar("Errore durante l'eliminazione.", 'error');
      }
    },
    onSerreAggiornate() {
      if (this.serra_selezionata) {
        const aggiornata = this.serre.find((s) => s.id === this.serra_selezionata.id);
        if (aggiornata) this.serra_selezionata = aggiornata;
      }
    },
    tipoLabel(tipo)  { return TIPO_LABELS[tipo]  || 'Serra'; },
    serraColor(tipo) { return TIPO_COLORS[tipo]  || '#546E7A'; },
    serraFill(tipo)  { return TIPO_FILLS[tipo]   || '#ECEFF1'; },
    _componenteInfos(prodotto) {
      const seen = new Set();
      const fallbacks = ['#EF9A9A', '#90CAF9', '#A5D6A7', '#FFE082'];
      const infos = [];
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        const key = `${c.id_specie}|${id_v ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const sp = this.specie_disponibili.find(s => s.id == c.id_specie);
        let img_url = null;
        if (sp && id_v) {
          const v = (sp.variety || []).find(v => v.id == id_v);
          if (v?.id_colore) img_url = `${process.env.VUE_APP_API_ROOT}/specie/img_colore/${v.id_colore}?t=${v.md5_colore || ''}`;
        }
        infos.push({ img_url, fallback: fallbacks[infos.length % fallbacks.length] });
        if (infos.length >= 4) break;
      }
      return infos;
    },
    _componenteRiepilogo(prodotto, cont) {
      const seen = new Set();
      const seen_specie = new Set();
      const righe = [];
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        const id_s = (c.id_serie  && c.id_serie  !== 'None') ? c.id_serie  : null;
        const key = `${c.id_specie}|${id_s ?? ''}|${id_v ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const sp = this.specie_disponibili.find(s => s.id == c.id_specie);
        if (sp?.nome) seen_specie.add(sp.nome);
        const ns = id_s ? this.nomeSerie(c.id_specie, id_s)   : null;
        const nv = id_v ? this.nomeVarieta(c.id_specie, id_v) : null;
        righe.push([ns, nv].filter(Boolean).join(' · ') || 'Generico');
        if (righe.length >= 4) break;
      }
      return {
        tipo_contenitore: cont?.tipo || null,
        specie_nomi: [...seen_specie].join(' / ') || null,
        righe,
      };
    },
    _sectorPath(i, n, r, cx, cy) {
      const a0 = (i / n) * 2 * Math.PI;
      const a1 = ((i + 1) / n) * 2 * Math.PI;
      const x0 = cx + r * Math.sin(a0);
      const y0 = cy - r * Math.cos(a0);
      const x1 = cx + r * Math.sin(a1);
      const y1 = cy - r * Math.cos(a1);
      const large = (a1 - a0) > Math.PI ? 1 : 0;
      return `M ${cx},${cy} L ${x0.toFixed(3)},${y0.toFixed(3)} A ${r},${r} 0 ${large},1 ${x1.toFixed(3)},${y1.toFixed(3)} Z`;
    },
    _varietaImgUrl(prodotto) {
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        if (!id_v) continue;
        const sp = this.specie_disponibili.find((s) => s.id == c.id_specie);
        if (!sp) continue;
        const varieta = (sp.variety || []).find((v) => v.id == id_v);
        if (!varieta || !varieta.id_colore) continue;
        return `${process.env.VUE_APP_API_ROOT}/specie/img_colore/${varieta.id_colore}?t=${varieta.md5_colore || ''}`;
      }
      return null;
    },
    productColor(prodotto) {
      if (!prodotto) return '#E8E8E8';
      if (prodotto.colore_contenitore) {
        const parsed = this.parseColorEntry(prodotto.colore_contenitore);
        if (parsed && parsed.hex) return parsed.hex;
      }
      const palette = [
        '#4CAF50','#2196F3','#FF9800','#E91E63','#9C27B0',
        '#00BCD4','#FF5722','#795548','#009688','#3F51B5',
      ];
      return palette[(prodotto.id || 0) % palette.length];
    },
    mostraSnackbar(message, color) {
      this.snackbar = { visible: true, message, color };
    },
  },
};
</script>

<style scoped>
.fila-swatch {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.fila-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}
</style>
